import React, { createContext, useEffect, useReducer, useState } from 'react';
import {
  PipelineReviewConfig,
  SuperDuperFiestaDataClass,
  SuperDuperFiestaDataSource,
  SuperDuperFiestaEntity,
  SuperDuperFiestaStage,
} from '../data/SuperDuperFiestaData';
import useSuperDuperFiestaDataClasses from '../components/hooks/UseSuperDuperFiestaDataClasses';
import useSuperDuperFiestaDataSources from '../components/hooks/UseSuperDuperFiestaDataSources';
import useSuperDuperFiestaEntities from '../components/hooks/UseSuperDuperFiestaEntities';
import useSuperDuperFiestaStages from '../components/hooks/UseSuperDuperFiestaStages';

interface IPipelineReviewState {
  entity: SuperDuperFiestaEntity | null;
  stage: SuperDuperFiestaStage | null;
  dataSource: SuperDuperFiestaDataSource | null;
  dataClass: SuperDuperFiestaDataClass | null;
  issueId: number | null;
  pageNumber: string | null;
  pageSize: string | null;
}

interface IPipelineReviewAction {
  type: 'update_entity' | 'update_stage' | 'update_dataSource' | 'update_dataClass' | 'update_selectedIssue' | 'reset';
  entity?: SuperDuperFiestaEntity | null;
  stage?: SuperDuperFiestaStage | null;
  dataSource?: SuperDuperFiestaDataSource | null;
  dataClass?: SuperDuperFiestaDataClass | null;
  issueId?: number | null;
  pageNumber?: string | null;
  pageSize?: string | null;
}

const initialState = {
  entity: null,
  stage: null,
  dataSource: null,
  dataClass: null,
  issueId: null,
  pageNumber: null,
  pageSize: null,
};

function pipelineReviewReducer(state: IPipelineReviewState, action: IPipelineReviewAction): IPipelineReviewState {
  const { type } = action;

  switch (type) {
    case 'update_entity': {
      return { ...state, entity: action.entity ?? null };
    }
    case 'update_stage': {
      return { ...state, stage: action.stage ?? null };
    }
    case 'update_dataSource': {
      return { ...state, dataSource: action.dataSource ?? null };
    }
    case 'update_dataClass': {
      return { ...state, dataClass: action.dataClass ?? null };
    }
    case 'update_selectedIssue': {
      return { ...state, issueId: action.issueId ?? null };
    }
    case 'reset': {
      return { ...initialState };
    }
    default:
      return state;
  }
}

export interface IPipelineReviewContextData {
  dataClasses: ReadonlyArray<SuperDuperFiestaDataClass>;
  dataSources: ReadonlyArray<SuperDuperFiestaDataSource>;
  entities: ReadonlyArray<SuperDuperFiestaEntity>;
  stages: ReadonlyArray<SuperDuperFiestaStage>;
  pipelineReviewState: IPipelineReviewState;
  pipelineReviewParams: PipelineReviewConfig | null;
  resetPaginationModel: boolean;
}

export interface IPipelineReviewContext extends IPipelineReviewContextData {
  pipelineReviewDispatch: React.Dispatch<IPipelineReviewAction>;
  clearContextSelection: () => void;
  setResetPaginationModel: React.Dispatch<React.SetStateAction<boolean>>;
}

const PipelineReviewContext = createContext<IPipelineReviewContext>({
  dataClasses: [],
  dataSources: [],
  entities: [],
  stages: [],
  pipelineReviewParams: null,
  pipelineReviewState: initialState,
  pipelineReviewDispatch: () => {
    /* empty */
  },
  clearContextSelection: () => {
    /* empty */
  },
  resetPaginationModel: false,
  setResetPaginationModel: () => {
    /* empty */
  },
});

export const PipelineReviewContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const dataClasses = useSuperDuperFiestaDataClasses();
  const dataSources = useSuperDuperFiestaDataSources();
  const entities = useSuperDuperFiestaEntities();
  const stages = useSuperDuperFiestaStages();

  const [pipelineReviewParams, setPipelineReviewParams] = useState<PipelineReviewConfig | null>(null);
  const [pipelineReviewState, pipelineReviewDispatch] = useReducer(pipelineReviewReducer, initialState);
  const [resetPaginationModel, setResetPaginationModel] = useState<boolean>(false);

  const clearContextSelection = () => {
    pipelineReviewDispatch({ type: 'reset' });
  };

  useEffect(() => {
    if (pipelineReviewState.entity === null) {
      setPipelineReviewParams(null);
    } else {
      const queryParams: PipelineReviewConfig = {
        entity: pipelineReviewState.entity.name,
        stage: pipelineReviewState?.stage?.name || 'selection',
      };

      if (pipelineReviewState.dataSource !== null) {
        queryParams['dataSourceId'] = pipelineReviewState.dataSource.dataSourceId;
      }
      if (pipelineReviewState.dataClass !== null) {
        queryParams['dataClassId'] = pipelineReviewState.dataClass.dataClassId;
      }
      if (pipelineReviewState.issueId !== null) {
        queryParams['selectedIssueId'] = pipelineReviewState.issueId.toString();
      }
      setPipelineReviewParams(queryParams);
    }
  }, [pipelineReviewState]);

  return (
    <PipelineReviewContext.Provider
      value={{
        dataClasses,
        dataSources,
        entities,
        stages,
        pipelineReviewState,
        pipelineReviewDispatch,
        pipelineReviewParams,
        clearContextSelection,
        resetPaginationModel,
        setResetPaginationModel,
      }}
    >
      {children}
    </PipelineReviewContext.Provider>
  );
};

export default PipelineReviewContext;
